/* eslint-disable no-use-before-define */
import { createSvgIcon, Button, Box } from "@mui/material";
import { useState, useCallback, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

const colors = {
  moving: "rgba(60, 60, 60, 0.4)",
  blocked: "#2C2B2B"
};

const defaultIconSize = 40;
const defaultSize = { width: 250, height: 200 };
const defaultPosition = { x: 100, y: 100 };
const defaultMouseClickOffset = {
  x: 0,
  y: 0
};

const DRAG_STATE = {
  STOPPED_DRAGGING: "stopped",
  OFF: "off",
  SIZE: "size",
  POSITION: "position"
};

const ResizeIcon = createSvgIcon(
  <>
    <rect width="100%" height="100%" fill="lightgray" strokeWidth="2px" />
    <path
      style={{ transformOrigin: "center", rotate: "90deg" }}
      d="M21 11V3h-8l3.29 3.29-10 10L3 13v8h8l-3.29-3.29 10-10z"
    />
  </>,
  "Resize"
);

const RepositionIcon = createSvgIcon(
  <>
    <rect width="100%" height="100%" fill="lightgray" strokeWidth="2px" />
    <path d="M10 9h4V6h3l-5-5-5 5h3v3zm-1 1H6V7l-5 5 5 5v-3h3v-4zm14 2-5-5v3h-3v4h3v3l5-5zm-9 3h-4v3H7l5 5 5-5h-3v-3z" />
  </>,
  "Reposition"
);

export default function CutoutWrapper({
  zoomController,
  active,
  isModalCutout = false,
  cutoutSize,
  setCutoutSize,
  cutoutPosition,
  setCutoutPosition,
  modalCutoutSize,
  setModalCutoutSize,
  modalCutoutPosition,
  setModalCutoutPosition
}) {
  const [cutoutInitialPosition, setCutoutInitialPosition] = useState();
  const [cutoutInitialSize, setCutoutInitialSize] = useState();
  if (active && !cutoutInitialSize) {
    setCutoutInitialSize(() => {
      const initialSize = isModalCutout ? modalCutoutSize : cutoutSize;
      const selectedSize = initialSize || defaultSize;
      if (Number.isNaN(selectedSize?.width) || Number.isNaN(selectedSize?.height)) return defaultSize;
      return selectedSize;
    });
  }
  if (active && !cutoutInitialPosition) {
    setCutoutInitialPosition(() => {
      const initialPosition = isModalCutout ? modalCutoutPosition : cutoutPosition;
      const selectedPosition = initialPosition || defaultPosition;
      if (Number.isNaN(selectedPosition?.x) || Number.isNaN(selectedPosition?.y)) return defaultPosition;
      return selectedPosition;
    });
  }
  if (!active && (cutoutInitialPosition != null || cutoutInitialSize != null)) {
    setCutoutInitialSize(null);
    setCutoutInitialPosition(null);
  }
  if (!active || !cutoutInitialSize || !cutoutInitialPosition) return null;
  if (isModalCutout) {
    return (
      <Cutout
        cutoutId="cutoutmaterial"
        initialPosition={cutoutInitialPosition}
        initialSize={cutoutInitialSize}
        setCutoutPosition={setModalCutoutPosition}
        setCutoutSize={setModalCutoutSize}
        zoomController={zoomController}
      />
    );
  }
  return (
    <Cutout
      cutoutId="cutoutbook"
      initialPosition={cutoutInitialPosition}
      initialSize={cutoutInitialSize}
      setCutoutPosition={setCutoutPosition}
      setCutoutSize={setCutoutSize}
      zoomController={zoomController}
    />
  );
}

function Cutout({ zoomController, cutoutId, initialPosition, initialSize, setCutoutSize, setCutoutPosition }) {
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);

  const [dragState, setDragState] = useState(DRAG_STATE.OFF);
  const [mouseClickOffset, setMouseClickOffset] = useState(defaultMouseClickOffset);
  const [wrapperRef, setWrapperRef] = useState(defaultMouseClickOffset);

  const dragStateOff = DRAG_STATE === DRAG_STATE.OFF || DRAG_STATE.STOPPED_DRAGGING;

  // TEST: out of effect...
  useEffect(() => {
    // sync size after changed with db
    if (dragState === DRAG_STATE.STOPPED_DRAGGING) {
      setCutoutSize(size);
      setCutoutPosition(position);
      setDragState(DRAG_STATE.OFF);
    }
  }, [dragState, position, setCutoutPosition, setCutoutSize, size]);

  const updateMouseOffset = useCallback(event => {
    const rect = event.target.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setMouseClickOffset({ x, y });
  }, []);

  const handleRepositionStart = useCallback(
    event => {
      zoomController.pause();
      updateMouseOffset(event);
      setDragState(DRAG_STATE.POSITION);
    },
    [updateMouseOffset, zoomController]
  );

  const handleResizeStart = useCallback(
    event => {
      zoomController.pause();
      updateMouseOffset(event);
      setDragState(DRAG_STATE.SIZE);
    },
    [updateMouseOffset, zoomController]
  );

  const handleStopDrag = useCallback(
    event => {
      if (dragState !== DRAG_STATE.SIZE && dragState !== DRAG_STATE.POSITION) {
        return;
      }
      zoomController.resume();
      setMouseClickOffset({ x: 0, y: 0 });
      setDragState(DRAG_STATE.STOPPED_DRAGGING);
    },
    [dragState, zoomController]
  );

  useEffect(() => {
    document.addEventListener("pointerup", handleStopDrag);

    return () => {
      document.removeEventListener("pointerup", handleStopDrag);
    };
  }, [handleStopDrag]);

  const iconSize = useMemo(() => {
    const scale = zoomController.getScale();
    return defaultIconSize / scale;
  }, [zoomController]);

  // sets size & position during mousemove
  const handleMouseMove = useCallback(
    event => {
      if (dragState !== DRAG_STATE.SIZE && dragState !== DRAG_STATE.POSITION) return;

      const scale = zoomController.getScale();
      const boundingRect = wrapperRef.getBoundingClientRect();
      const wrapperWidth = boundingRect.width / scale;
      const wrapperHeight = boundingRect.height / scale;

      // get parent values
      const left = boundingRect?.left ?? 0;
      const top = boundingRect?.top ?? 0;

      switch (dragState) {
        case DRAG_STATE.POSITION: {
          const offsetX = (event.clientX - left + (defaultIconSize - mouseClickOffset.x)) / scale;
          const offsetY = (event.clientY - top - mouseClickOffset.y) / scale;
          let newPositionX = size.width > 0 ? offsetX - size.width : 0;
          let newPositionY = size.height > 0 ? offsetY : 0;
          if (newPositionX < 0) newPositionX = 0;
          if (newPositionY < 0) newPositionY = 0;

          if (size.width + newPositionX > wrapperWidth) {
            newPositionX = wrapperWidth - size.width;
          }
          if (size.height + newPositionY > wrapperHeight) {
            newPositionY = wrapperHeight - size.height;
          }

          const newPos = {
            x: newPositionX,
            y: newPositionY
          };

          setPosition(newPos);
          break;
        }
        case DRAG_STATE.SIZE: {
          const offsetX = (event.clientX - left + (defaultIconSize - mouseClickOffset.x)) / scale;
          const offsetY = (event.clientY - top + (defaultIconSize - mouseClickOffset.y)) / scale;
          let newSizeW = offsetX - position.x;
          let newSizeH = offsetY - position.y;

          if (position.x + newSizeW > wrapperWidth) {
            newSizeW = wrapperWidth - position.x;
          }
          if (position.y + newSizeH > wrapperHeight) {
            newSizeH = wrapperHeight - position.y;
          }

          setSize({
            width: Math.max(newSizeW, iconSize),
            height: Math.max(newSizeH, iconSize * 2)
          });
          break;
        }
        default:
          break;
      }
    },
    [
      dragState,
      iconSize,
      mouseClickOffset.x,
      mouseClickOffset.y,
      position.x,
      position.y,
      size.height,
      size.width,
      wrapperRef,
      zoomController
    ]
  );

  /*
    draws 2 rects:
      first one around the whole of the element
      then for the actual cutout inside that.
      necessary for the "fill-rule: evenodd"
  */
  let pathString = "";
  let resizeIconPosition = "";
  let repositionIconPosition = "";
  if (wrapperRef) {
    const wrapperWidth = wrapperRef?.offsetWidth ?? 0;
    const wrapperHeight = wrapperRef?.offsetHeight ?? 0;
    pathString = `M0,0 l${wrapperWidth},0 l0,${wrapperHeight} l-${wrapperWidth},0 l0,-${wrapperHeight}
          M${position.x},${position.y} l${size.width},0 l0,${size.height} l${-size.width},0 l0,${-size.height}`;

    const posX = position.x + size.width - iconSize;

    const repositionY = position.y;
    const resizeY = position.y + size.height - iconSize;
    resizeIconPosition = {
      x: posX,
      y: resizeY
    };
    repositionIconPosition = {
      x: posX,
      y: repositionY
    };
  }

  // push cutout into frame when switching to singlePage and its out of frame
  if (wrapperRef && wrapperRef.getBoundingClientRect && dragState === DRAG_STATE.OFF) {
    const boundingRect = wrapperRef.getBoundingClientRect();
    const scale = zoomController.getScale();
    const wrapperWidth = boundingRect.width / scale;
    const wrapperHeight = boundingRect.height / scale;

    let newPositionX = position.x;
    let newPositionY = position.y;
    if (position.x < 0) newPositionX = 0;
    if (position.y < 0) newPositionY = 0;

    // make sure: W is at max of wrapperwidth, min iconSize
    const newWidth = Math.min(wrapperWidth, Math.max(size.width, iconSize));
    const newHeight = Math.min(wrapperHeight, Math.max(size.height, iconSize * 2));

    // if it is oushing out of bounds, move to side until it fits
    // max size is wrapperSize, so size does not need to be changed
    if (newWidth + newPositionX > wrapperWidth) {
      newPositionX = wrapperWidth - size.width;
    }
    if (newHeight + newPositionY > wrapperHeight) {
      newPositionY = wrapperHeight - size.height;
    }
    if (newPositionX !== position.x || newPositionY !== position.y) {
      setPosition({
        x: newPositionX,
        y: newPositionY
      });
    }
    if (newWidth !== size.width || newHeight !== size.height) {
      setSize({
        width: newWidth,
        height: newHeight
      });
    }
  }

  /*
    conventional ref dont work for initial render of pathString.
    use measuredRef as per react recommendation
  */
  const setRef = useCallback(node => {
    setWrapperRef(node);
  }, []);

  return (
    <Box
      ref={setRef}
      style={{
        width: "calc(100% + 1px)",
        height: "calc(100% + 2px)",
        position: "absolute",
        touchAction: "none"
      }}
      onPointerMove={handleMouseMove}
    >
      <svg width="100%" height="100%" pointerEvents={dragStateOff ? "none" : "auto"}>
        <defs>
          <clipPath id={cutoutId} clipRule="evenodd">
            <path id="cutoutBox" d={pathString} fill="black" stroke="gray" strokeDasharray="10,5" strokeWidth="5px" />
          </clipPath>
        </defs>
        <g>
          <rect
            width="100%"
            height="100%"
            clipPath={`url(#${cutoutId})`}
            pointerEvents="fill"
            fill={dragStateOff ? colors.blocked : colors.moving}
          />
        </g>
      </svg>
      <Button
        onPointerDown={handleRepositionStart}
        style={{
          position: "absolute",
          left: repositionIconPosition.x,
          top: repositionIconPosition.y,
          padding: 0,
          cursor: "move",
          pointerEvents: dragStateOff ? "auto" : "none",
          width: "max-content",
          minWidth: 0,
          color: "black"
        }}
      >
        <RepositionIcon
          fill="white"
          width={iconSize}
          height={iconSize}
          style={{
            height: iconSize,
            width: iconSize
          }}
          cursor="move"
        />
      </Button>
      <Button
        onPointerDown={handleResizeStart}
        style={{
          position: "absolute",
          left: resizeIconPosition.x,
          top: resizeIconPosition.y,
          padding: 0,
          cursor: "nwse-resize",
          pointerEvents: dragStateOff ? "auto" : "none",
          width: "max-content",
          minWidth: 0,
          color: "black"
        }}
      >
        <ResizeIcon
          fill="blue"
          width={iconSize}
          height={iconSize}
          style={{
            height: iconSize,
            width: iconSize
          }}
        />
      </Button>
    </Box>
  );
}

CutoutWrapper.propTypes = {
  zoomController: PropTypes.object.isRequired,
  active: PropTypes.bool.isRequired,
  isModalCutout: PropTypes.bool,
  cutoutSize: PropTypes.object,
  setCutoutSize: PropTypes.func.isRequired,
  cutoutPosition: PropTypes.object,
  setCutoutPosition: PropTypes.func.isRequired,
  modalCutoutSize: PropTypes.object,
  setModalCutoutSize: PropTypes.func.isRequired,
  modalCutoutPosition: PropTypes.object,
  setModalCutoutPosition: PropTypes.func.isRequired
};
Cutout.propTypes = {
  zoomController: PropTypes.object.isRequired,
  cutoutId: PropTypes.string.isRequired,
  initialPosition: PropTypes.object,
  setCutoutSize: PropTypes.func.isRequired,
  initialSize: PropTypes.object,
  setCutoutPosition: PropTypes.func.isRequired
};
